export interface PvPBotConfig {
  readonly decisionIntervalMs: number;
  readonly cooldownMs: number;
  readonly smaShortWindow: number; // points
  readonly smaLongWindow: number; // points
  readonly momentumLookbackPoints: number;
  readonly takeProfitPct: number; // e.g., 0.06 = 6%
  readonly stopLossPct: number; // e.g., 0.05 = 5%
  readonly minHoldMs: number;
}

export type PvPBotDecision = "buy" | "sell" | undefined;

export interface PvPBotInputPoint {
  readonly time: number; // ms
  readonly price: number;
}

export class PvPBot {
  private readonly cfg: PvPBotConfig;
  private lastDecisionAtMs: number;
  private lastTradeAtMs: number;

  constructor(config?: Partial<PvPBotConfig>) {
    this.cfg = {
      decisionIntervalMs: 350,
      cooldownMs: 2300,
      smaShortWindow: 6,
      smaLongWindow: 20,
      momentumLookbackPoints: 12,
      takeProfitPct: 0.06,
      stopLossPct: 0.05,
      minHoldMs: 1200,
      ...config,
    } as PvPBotConfig;
    this.lastDecisionAtMs = 0;
    this.lastTradeAtMs = -Infinity;
  }

  public reset(): void {
    this.lastDecisionAtMs = 0;
    this.lastTradeAtMs = -Infinity;
  }

  public update(
    nowMs: number,
    visiblePoints: ReadonlyArray<PvPBotInputPoint>,
    isHolding: boolean,
    buyPrice?: number,
    buysLeft?: number,
    holdStartTimeMs?: number
  ): PvPBotDecision {
    if (
      visiblePoints.length < Math.max(this.cfg.smaLongWindow, 2) ||
      nowMs - this.lastDecisionAtMs < this.cfg.decisionIntervalMs ||
      nowMs - this.lastTradeAtMs < this.cfg.cooldownMs
    ) {
      return undefined;
    }
    this.lastDecisionAtMs = nowMs;

    const last = visiblePoints[visiblePoints.length - 1];
    const prices = visiblePoints.map((p) => p.price);

    const sma = (window: number): number => {
      if (prices.length < window) return prices[prices.length - 1] ?? 0;
      let sum = 0;
      for (let i = prices.length - window; i < prices.length; i++)
        sum += prices[i];
      return sum / window;
    };

    const smaShort = sma(this.cfg.smaShortWindow);
    const smaLong = sma(this.cfg.smaLongWindow);

    const momentumStartIdx = Math.max(
      0,
      prices.length - this.cfg.momentumLookbackPoints
    );
    const momentum =
      (prices[prices.length - 1] - prices[momentumStartIdx]) /
      Math.max(1e-9, prices[momentumStartIdx]);

    // If not holding: consider BUY if short SMA above long and positive momentum
    if (!isHolding) {
      if ((buysLeft ?? 0) <= 0) return undefined;
      const bullish = smaShort > smaLong && momentum > 0.01;
      if (bullish) {
        this.lastTradeAtMs = nowMs;
        return "buy";
      }
      return undefined;
    }

    // If holding: consider SELL on take-profit, stop-loss, or bearish cross with momentum
    if ((buyPrice ?? 0) > 0) {
      const changePct =
        (last.price - (buyPrice as number)) /
        Math.max(1e-9, buyPrice as number);
      const heldMs = Math.max(0, nowMs - (holdStartTimeMs ?? nowMs));
      const bearish = smaShort < smaLong && momentum < -0.005;
      const takeProfit = changePct >= this.cfg.takeProfitPct;
      const stopLoss = changePct <= -this.cfg.stopLossPct;
      if (takeProfit || stopLoss || (bearish && heldMs >= this.cfg.minHoldMs)) {
        this.lastTradeAtMs = nowMs;
        return "sell";
      }
    }

    return undefined;
  }
}
